import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../lib/apiBaseUrl';
import './CryptoWallet.css';

const API_BASE_URL = getApiBaseUrl();

function CryptoWallet({ user, onBack }) {
  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [depositForm, setDepositForm] = useState({
    amount: '',
    paymentMethod: 'crypto'
  });
  const [withdrawForm, setWithdrawForm] = useState({
    amount: '',
    address: ''
  });
  const [activeTab, setActiveTab] = useState('balance');
  
  // Кеширование для предотвращения лишних запросов
  const [lastBalanceFetch, setLastBalanceFetch] = useState(0);
  const [lastTransactionsFetch, setLastTransactionsFetch] = useState(0);
  const CACHE_TIME = 5000; // 5 секунд кеш

  const fetchBalance = useCallback(async (force = false) => {
    const now = Date.now();
    if (!force && now - lastBalanceFetch < CACHE_TIME) {
      return; // Используем кеш
    }
    
    try {
      const response = await axios.get(`${API_BASE_URL}/api/wallet/balance`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setBalance(response.data.balance);
      setLastBalanceFetch(now);
    } catch (error) {
      console.error('Ошибка получения баланса:', error);
    }
  }, [lastBalanceFetch]);

  const fetchTransactions = useCallback(async (force = false) => {
    const now = Date.now();
    if (!force && now - lastTransactionsFetch < CACHE_TIME) {
      return; // Используем кеш
    }
    
    try {
      const response = await axios.get(`${API_BASE_URL}/api/wallet/transactions`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setTransactions(response.data.transactions);
      setLastTransactionsFetch(now);
    } catch (error) {
      console.error('Ошибка получения транзакций:', error);
    }
  }, [lastTransactionsFetch]);

  useEffect(() => {
    fetchBalance();
    fetchTransactions();
    
    // Обновляем данные каждые 10 секунд
    const interval = setInterval(() => {
      fetchBalance();
      fetchTransactions();
    }, 10000);
    
    return () => clearInterval(interval);
  }, [fetchBalance, fetchTransactions]);

  const handleDeposit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(`${API_BASE_URL}/api/wallet/deposit`, depositForm, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      setSuccess(`Платеж обрабатывается! ID транзакции: ${response.data.transactionId}`);
      setDepositForm({ amount: '', paymentMethod: 'crypto' });
      
      // Обновляем данные через несколько секунд
      setTimeout(() => {
        fetchBalance();
        fetchTransactions();
      }, 2000);

    } catch (error) {
      setError(error.response?.data?.error || 'Ошибка пополнения');
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(`${API_BASE_URL}/api/wallet/withdraw`, withdrawForm, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      setSuccess(`Запрос на вывод обрабатывается! ID транзакции: ${response.data.transactionId}`);
      setWithdrawForm({ amount: '', address: '' });
      
      // Обновляем данные через несколько секунд
      setTimeout(() => {
        fetchBalance();
        fetchTransactions();
      }, 2000);

    } catch (error) {
      setError(error.response?.data?.error || 'Ошибка вывода');
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('ru-RU').format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#00b894';
      case 'pending': return '#fdcb6e';
      case 'failed': return '#e17055';
      default: return '#74b9ff';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'Завершено';
      case 'pending': return 'Обработка';
      case 'failed': return 'Ошибка';
      default: return 'Неизвестно';
    }
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'deposit': return '⬇️';
      case 'withdraw': return '⬆️';
      default: return '💸';
    }
  };

  return (
    <div className="crypto-wallet">
      <div className="wallet-header">
        <h2>💎 Крипто-Кошелек</h2>
        <button className="btn btn-secondary" onClick={onBack}>
          ← Назад в лобби
        </button>
      </div>

      <div className="wallet-tabs">
        <button 
          className={`tab-btn ${activeTab === 'balance' ? 'active' : ''}`}
          onClick={() => setActiveTab('balance')}
        >
          💰 Баланс
        </button>
        <button 
          className={`tab-btn ${activeTab === 'deposit' ? 'active' : ''}`}
          onClick={() => setActiveTab('deposit')}
        >
          ⬇️ Пополнить
        </button>
        <button 
          className={`tab-btn ${activeTab === 'withdraw' ? 'active' : ''}`}
          onClick={() => setActiveTab('withdraw')}
        >
          ⬆️ Вывести
        </button>
        <button 
          className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          📊 История
        </button>
      </div>

      {error && (
        <div className="error">
          {error}
        </div>
      )}

      {success && (
        <div className="success">
          {success}
        </div>
      )}

      <div className="tab-content">
        {activeTab === 'balance' && (
          <div className="balance-tab">
            <div className="balance-card">
              <div className="balance-icon">💰</div>
              <div className="balance-amount">
                {formatAmount(balance)} SEKA
              </div>
              <div className="balance-label">Текущий баланс</div>
              <div className="balance-info">
                <p>Валюта: SEKA (Seka Game Token)</p>
                <p>Статус: Активен</p>
                <p>Последнее обновление: {new Date().toLocaleTimeString('ru-RU')}</p>
              </div>
            </div>

            <div className="quick-actions">
              <h3>Быстрые действия</h3>
              <div className="quick-buttons">
                <button 
                  className="btn btn-success"
                  onClick={() => setActiveTab('deposit')}
                >
                  Пополнить
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={() => setActiveTab('withdraw')}
                >
                  Вывести
                </button>
                <button 
                  className="btn"
                  onClick={fetchBalance}
                >
                  Обновить
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'deposit' && (
          <div className="deposit-tab">
            <div className="deposit-info">
              <h3>💎 Пополнение баланса</h3>
              <p>Пополните свой баланс с помощью криптовалют</p>
              <div className="info-cards">
                <div className="info-card">
                  <div className="info-icon">⏱️</div>
                  <div className="info-text">
                    <strong>Время подтверждения:</strong><br />
                    30 секунд - 5 минут
                  </div>
                </div>
                <div className="info-card">
                  <div className="info-icon">💳</div>
                  <div className="info-text">
                    <strong>Комиссия:</strong><br />
                    1% от суммы
                  </div>
                </div>
                <div className="info-card">
                  <div className="info-icon">🔒</div>
                  <div className="info-text">
                    <strong>Безопасность:</strong><br />
                    Полная защита данных
                  </div>
                </div>
              </div>
            </div>

            <form onSubmit={handleDeposit} className="deposit-form">
              <div className="form-group">
                <label htmlFor="depositAmount">Сумма пополнения (SEKA)</label>
                <input
                  type="number"
                  id="depositAmount"
                  value={depositForm.amount}
                  onChange={(e) => setDepositForm({ ...depositForm, amount: e.target.value })}
                  placeholder="Введите сумму"
                  min="1"
                  max="10000"
                  required
                />
                <div className="input-hint">
                  Минимум: 1 SEKA, Максимум: 10,000 SEKA
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="paymentMethod">Способ оплаты</label>
                <select
                  id="paymentMethod"
                  value={depositForm.paymentMethod}
                  onChange={(e) => setDepositForm({ ...depositForm, paymentMethod: e.target.value })}
                >
                  <option value="crypto">Криптовалюта</option>
                  <option value="card">Банковская карта</option>
                  <option value="wallet">Электронный кошелек</option>
                </select>
              </div>

              <button 
                type="submit" 
                className="btn btn-success deposit-btn"
                disabled={loading || !depositForm.amount}
              >
                {loading ? 'Обработка...' : `Пополнить ${depositForm.amount || 0} SEKA`}
              </button>
            </form>
          </div>
        )}

        {activeTab === 'withdraw' && (
          <div className="withdraw-tab">
            <div className="withdraw-info">
              <h3>💸 Вывод средств</h3>
              <p>Выведите средства на свой крипто-кошелек</p>
            </div>

            <form onSubmit={handleWithdraw} className="withdraw-form">
              <div className="form-group">
                <label htmlFor="withdrawAmount">Сумма вывода (SEKA)</label>
                <input
                  type="number"
                  id="withdrawAmount"
                  value={withdrawForm.amount}
                  onChange={(e) => setWithdrawForm({ ...withdrawForm, amount: e.target.value })}
                  placeholder="Введите сумму"
                  min="10"
                  max={balance}
                  required
                />
                <div className="input-hint">
                  Доступно: {formatAmount(balance)} SEKA
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="walletAddress">Адрес кошелька</label>
                <input
                  type="text"
                  id="walletAddress"
                  value={withdrawForm.address}
                  onChange={(e) => setWithdrawForm({ ...withdrawForm, address: e.target.value })}
                  placeholder="Введите адрес кошелька"
                  required
                />
              </div>

              <button 
                type="submit" 
                className="btn withdraw-btn"
                disabled={loading || !withdrawForm.amount || !withdrawForm.address}
              >
                {loading ? 'Обработка...' : `Вывести ${withdrawForm.amount || 0} SEKA`}
              </button>
            </form>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="history-tab">
            <div className="history-header">
              <h3>📊 История транзакций</h3>
              <button 
                className="btn btn-secondary"
                onClick={fetchTransactions}
              >
                Обновить
              </button>
            </div>

            {transactions.length === 0 ? (
              <div className="no-transactions">
                <p>Нет транзакций</p>
                <p>Пополните баланс, чтобы увидеть историю</p>
              </div>
            ) : (
              <div className="transactions-list">
                {transactions.map((transaction) => (
                  <div key={transaction.id} className="transaction-item">
                    <div className="transaction-icon">
                      {getTransactionIcon(transaction.type)}
                    </div>
                    <div className="transaction-details">
                      <div className="transaction-type">
                        {transaction.type === 'deposit' ? 'Пополнение' : 'Вывод'}
                      </div>
                      <div className="transaction-date">
                        {formatDate(transaction.createdAt)}
                      </div>
                    </div>
                    <div className="transaction-amount">
                      {transaction.type === 'deposit' ? '+' : '-'}{formatAmount(transaction.amount)} SEKA
                    </div>
                    <div 
                      className="transaction-status"
                      style={{ color: getStatusColor(transaction.status) }}
                    >
                      {getStatusText(transaction.status)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default CryptoWallet;
